/**
 * Services de configuration tenant.
 *
 * Lire est N1 : la cadence gouverne le ton d'un courrier, elle ne fait rien
 * sortir de l'entreprise. Écrire est N2 et réservé au patron — le réglage est
 * réversible, mais il décide à partir de quand un client reçoit une mise en
 * demeure. Ce n'est pas au conducteur de travaux de le déplacer.
 */
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import {
  DEFAULT_BRANDING,
  DEFAULT_MARGIN_TARGET,
  DEFAULT_REMINDER_CADENCE,
} from "./types.js";
import type {
  MarginTarget,
  OvertimeRates,
  ReminderCadence,
  SafetySettings,
  SubcontractingSettings,
  TenantBranding,
  TenantConfig,
} from "./types.js";
import type {
  ReadTenantConfigInput,
  SaveBrandingInput,
  SaveMarginTargetInput,
  SaveOvertimeRatesInput,
  SaveSafetySettingsInput,
  SaveSubcontractingSettingsInput,
  SaveReminderCadenceInput,
} from "./schema.js";

export interface TenantConfigRepository {
  /**
   * La configuration du tenant courant.
   *
   * Renvoie toujours une configuration, jamais `null` : un tenant sans ligne
   * `tenant_config` — ou avec une ligne dont le bloc `reminders` est absent —
   * relève des valeurs par défaut, pas d'un cas d'erreur que chaque appelant
   * devrait retraiter.
   */
  read(ctx: ExecutionContext): Promise<TenantConfig>;

  saveReminders(
    ctx: ExecutionContext,
    cadence: ReminderCadence,
  ): Promise<TenantConfig>;

  saveBranding(
    ctx: ExecutionContext,
    branding: TenantBranding,
  ): Promise<TenantConfig>;

  saveMarginTarget(
    ctx: ExecutionContext,
    targetPercent: MarginTarget,
  ): Promise<TenantConfig>;

  saveOvertimeRates(ctx: ExecutionContext, rates: OvertimeRates): Promise<TenantConfig>;

  saveSafetySettings(ctx: ExecutionContext, settings: SafetySettings): Promise<TenantConfig>;

  saveSubcontractingSettings(ctx: ExecutionContext, settings: SubcontractingSettings): Promise<TenantConfig>;
}

export function createTenantConfigServices(repo: TenantConfigRepository) {
  const lireConfiguration = defineService<ReadTenantConfigInput, TenantConfig>({
    name: "lire_configuration",
    level: ActionLevel.Read,
    handler: (ctx) => repo.read(ctx),
  });

  const enregistrerCadenceRelance = defineService<
    SaveReminderCadenceInput,
    TenantConfig
  >({
    name: "enregistrer_cadence_relance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.saveReminders(ctx, input.cadence),
  });

  const enregistrerIdentiteVisuelle = defineService<SaveBrandingInput, TenantConfig>({
    name: "enregistrer_identite_visuelle",
    level: ActionLevel.ReversibleWrite,
    // Le logo et la couleur apparaissent sur l'écran de connexion, servis
    // sans authentification à qui visite le domaine du client. C'est
    // l'entreprise qui s'affiche, pas un réglage d'agrément.
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.saveBranding(ctx, input.branding),
  });

  const enregistrerObjectifMarge = defineService<SaveMarginTargetInput, TenantConfig>({
    name: "enregistrer_objectif_marge",
    level: ActionLevel.ReversibleWrite,
    // Même porte que la cadence : le réglage est réversible et ne fait rien
    // sortir, mais il décide de ce que le tableau de bord appelle « en dessous
    // de l'objectif ». Un conducteur qui abaisserait la visée ferait passer au
    // vert des chantiers que le patron voulait voir en rouge.
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.saveMarginTarget(ctx, input.targetPercent),
  });

  /**
   * Régler les taux de majoration.
   *
   * Réservé au patron : un taux abaissé ferait paraître rentables des
   * chantiers qui ne le sont pas. Ne recalcule aucune semaine passée — même
   * discipline que le coût horaire figé à la saisie.
   */
  const enregistrerMajorations = defineService<SaveOvertimeRatesInput, TenantConfig>({
    name: "enregistrer_majorations",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) =>
      repo.saveOvertimeRates(ctx, {
        complementaryRate: input.complementaryRate,
        firstRate: input.firstRate,
        secondRate: input.secondRate,
      }),
  });

  /**
   * Régler la fréquence des quarts d'heure sécurité.
   *
   * Réservé au patron : c'est lui qui répond de la prévention, et une fréquence
   * allongée par un autre ferait taire le rappel sans qu'il le sache.
   */
  const enregistrerFrequenceSecurite = defineService<SaveSafetySettingsInput, TenantConfig>({
    name: "enregistrer_frequence_securite",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) =>
      repo.saveSafetySettings(ctx, { talkFrequencyDays: input.talkFrequencyDays }),
  });

  /**
   * Appliquer, ou non, le seuil de 5 000 € HT de l'obligation de vigilance.
   *
   * Réservé au patron : c'est lui qui répond solidairement d'un sous-traitant
   * qui n'a pas déclaré ses salariés, et activer le seuil fait taire des
   * relances.
   */
  const enregistrerReglagesSousTraitance = defineService<SaveSubcontractingSettingsInput, TenantConfig>({
    name: "enregistrer_reglages_sous_traitance",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) =>
      repo.saveSubcontractingSettings(ctx, {
        applyVigilanceThreshold: input.applyVigilanceThreshold,
        vigilanceBasis: input.vigilanceBasis,
      }),
  });

  return {
    lireConfiguration,
    enregistrerCadenceRelance,
    enregistrerIdentiteVisuelle,
    enregistrerObjectifMarge,
    enregistrerMajorations,
    enregistrerFrequenceSecurite,
    enregistrerReglagesSousTraitance,
  };
}

export type TenantConfigServices = ReturnType<typeof createTenantConfigServices>;

/**
 * Complète une cadence partiellement renseignée.
 *
 * Le bloc `reminders` de `tenant_config.settings` est du JSON libre : rien en
 * base n'empêche qu'il arrive amputé d'un palier, ou avec une valeur d'un
 * autre type. Plutôt que de laisser un `undefined` remonter jusqu'au calcul
 * du ton — où il produirait silencieusement un niveau 4 — on retombe palier
 * par palier sur la valeur par défaut.
 */
export function cadenceFrom(raw: unknown): ReminderCadence {
  const source = (raw ?? {}) as Record<string, unknown>;
  const pick = (key: keyof ReminderCadence): number => {
    const value = source[key];
    return typeof value === "number" && Number.isFinite(value) && value >= 0
      ? Math.trunc(value)
      : DEFAULT_REMINDER_CADENCE[key];
  };
  return {
    preventiveDaysBefore: pick("preventiveDaysBefore"),
    friendlyDaysAfter: pick("friendlyDaysAfter"),
    firmDaysAfter: pick("firmDaysAfter"),
    formalDaysAfter: pick("formalDaysAfter"),
  };
}

/**
 * Complète une identité visuelle partiellement renseignée.
 *
 * Même raison que `cadenceFrom` : `tenant_config.branding` est du JSON libre,
 * et une couleur absente ou mal formée qui remonterait telle quelle jusqu'aux
 * variables CSS produirait une interface sans accent — sans que rien ne
 * signale pourquoi.
 */
export function brandingFrom(raw: unknown): TenantBranding {
  const source = (raw ?? {}) as Record<string, unknown>;

  const logo = source.logo_url ?? source.logoUrl;
  const color = source.primary_color ?? source.primaryColor;

  const couleur =
    typeof color === "string" && /^#[0-9a-fA-F]{6}$/.test(color.trim())
      ? color.trim().toUpperCase()
      : DEFAULT_BRANDING.primaryColor;

  return {
    // `https` seulement, comme à la saisie : la valeur relue peut venir d'une
    // écriture antérieure au schéma, ou d'une intervention directe en base.
    logoUrl:
      typeof logo === "string" && logo.trim().startsWith("https://")
        ? logo.trim()
        : null,
    primaryColor: couleur,
  };
}

/**
 * Relit l'objectif de marge posé dans `settings`.
 *
 * Même raison que `cadenceFrom` et `brandingFrom` : le bloc est du JSON libre,
 * et rien en base n'empêche qu'il arrive sous une autre forme — écriture
 * antérieure au schéma, intervention directe, valeur recopiée depuis un
 * tableur. Tout ce qui n'est pas un nombre utilisable vaut « pas d'objectif »,
 * qui est un état prévu et affichable, plutôt qu'un `NaN` qui remonterait
 * jusqu'à la largeur d'une jauge.
 */
export function marginTargetFrom(raw: unknown): MarginTarget {
  const source = (raw ?? {}) as Record<string, unknown>;
  const value = source.target_percent ?? source.targetPercent;
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_MARGIN_TARGET;
  if (value < 0 || value > 100) return DEFAULT_MARGIN_TARGET;
  return Math.round(value * 10) / 10;
}
